import { ArrowDownIcon } from '@radix-ui/react-icons'
import { useEffect, useState } from 'react'
import { prefersReducedMotion } from '#app/utils/motion.ts'

/**
 * A floating "next concert" pill that scrolls to the next concert card, its
 * arrow turning to point up once the card has been scrolled past. It steps
 * aside while that card is on screen, and — when `hideWhile` names an element
 * (e.g. the hero, which has its own call to action) — while that element is
 * visible.
 */
export function NextEventFab({
	nextEventId,
	hideWhile,
}: {
	nextEventId: string | null
	/** CSS selector of an element that makes the pill redundant while visible */
	hideWhile?: string
}) {
	const [target, setTarget] = useState<'onscreen' | 'above' | 'below'>(
		'onscreen',
	)
	const [blockerVisible, setBlockerVisible] = useState(false)

	// measured on every scroll rather than observed: a jump link (e.g. to the
	// tickets) can skip right past the card without it ever intersecting
	useEffect(() => {
		if (!nextEventId) return
		const el = document.getElementById(`event-${nextEventId}`)
		if (!el) return
		const update = () => {
			const { top, bottom } = el.getBoundingClientRect()
			setTarget(
				bottom <= 0
					? 'above'
					: top >= window.innerHeight
						? 'below'
						: 'onscreen',
			)
		}
		update()
		window.addEventListener('scroll', update, { passive: true })
		window.addEventListener('resize', update)
		return () => {
			window.removeEventListener('scroll', update)
			window.removeEventListener('resize', update)
		}
	}, [nextEventId])

	useEffect(() => {
		if (!hideWhile || typeof IntersectionObserver === 'undefined') return
		const el = document.querySelector(hideWhile)
		if (!el) return
		const observer = new IntersectionObserver(([entry]) => {
			setBlockerVisible(Boolean(entry?.isIntersecting))
		})
		observer.observe(el)
		return () => observer.disconnect()
	}, [hideWhile])

	if (!nextEventId || target === 'onscreen' || blockerVisible) return null

	return (
		<button
			type="button"
			className="fixed right-4 bottom-28 z-30 inline-flex min-h-12 animate-slide-top items-center gap-2 rounded-full bg-[#f6d77a] px-4 py-3 font-semibold text-[#1c1a22] shadow-[0_14px_30px_-12px_rgb(40_25_10/0.55)] ring-1 ring-black/5 transition-transform hover:-translate-y-0.5 motion-reduce:animate-none sm:bottom-24"
			aria-label={`Scroll ${target === 'above' ? 'opp' : 'ned'} til neste konsert`}
			onClick={() => {
				document.getElementById(`event-${nextEventId}`)?.scrollIntoView({
					behavior: prefersReducedMotion() ? 'auto' : 'smooth',
					block: 'center',
				})
			}}
		>
			<ArrowDownIcon
				className={`h-5 w-5 transition-transform duration-300 motion-reduce:transition-none ${target === 'above' ? 'rotate-180' : ''}`}
				aria-hidden="true"
			/>
			Neste konsert
		</button>
	)
}
